import { ritzDataContext, selectLoggedInAgent } from '@moderepo/mode-data-state-ritz';
import { Agent, EntityClass } from '@moderepo/mode-internal-apis';
import { useContext, useMemo } from 'react';
import { CustomizedEntityDetailsPageLayout, EntityClassViewSettings, isEntityClassUISettings } from '.';
import { ALL_PREDEFINED_ENTITY_DETAILS_LAYOUTS, DEFAULT_ENTITY_DETAILS_COMPONENT_PLACEMENT, DEFAULT_ENTITY_DETAILS_LAYOUT } from '..';
import { useSelectProject } from '../../utils/dataStateHooks';
import { ProjectTypeHandlersManager } from '../../utils/ritzExtensionUtils';
import { SettingsErrorLogger } from '../../utils/logger';
import { EntityDetailsPageLayout, EntityDetailsCompsPlacement } from './EntityDetailsLayouts';



/**
 * Apply/Merge the customization object to the layout object. First, copy the layout object and then replace the attributes value in layout object
 * with the values from customization's that are not undefined.
 */
export const applyLayoutCustomization = (
    layout: EntityDetailsPageLayout, customization: CustomizedEntityDetailsPageLayout | undefined,
): EntityDetailsPageLayout => {
    if (customization) {
        return {
            ...layout,
            templateRows   : customization.templateRows ?? layout.templateRows,
            templateColumns: customization.templateColumns ?? layout.templateColumns,
            smallScreen    : customization.smallScreen ? { ...layout.smallScreen, ...customization.smallScreen } : layout.smallScreen,
            mediumScreen   : customization.mediumScreen ? { ...layout.mediumScreen, ...customization.mediumScreen } : layout.mediumScreen,
        };
    }

    return layout;
};



/**
 * Check if the specified componentsPlacement is valid. A componentsPlacement is invalid if places components in an areas not defined in the layout.
 * E.g.
 *      layout = {
 *          templateAreas: [
 *              ["top_left", "top_right"],
 *              ["bottom", "bottom"]
 *          ]
 *      }
 *
 * Invalid componentsPlacement
 *      componentsPlacement: {
 *          "top_left": [children],
 *          "center": [metric, ranking],        // this is invalid because "center" is not an area defined in layout.
 *      }
 */
export const isValidComponentsPlacement = (
    layout: EntityDetailsPageLayout,
    componentsPlacement: EntityDetailsCompsPlacement,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): boolean => {
    const configPath = [...(currentConfigPath ?? [])];

    /**
     * templateAreas is a double array of area names so we will flatten to make it easier to check
     */
    const templateAreaNames = layout.templateAreas.flat();

    // For each areaName used in customCompsPlacement, see if it exist in the list of templateAreaNames
    if (!Object.keys(componentsPlacement).every((areaName) => {
        if (!templateAreaNames.includes(areaName)) {
            return errorLogger?.logError({
                message: `Invalid area name. The area name must be one of the following [${templateAreaNames.map((areaName) => {
                    return `"${areaName}"`;
                }).join(', ')}]`,
                value: areaName,
                path : [...configPath, areaName],
            }) ?? false;
        }
        return true;
    })) {
        return false;
    }

    return true;
};



/**
 * Check the customLayout and customCompsPlacement objects specified in the entityClass UI Setting to see if they are valid. If they are,
 * return the customLayout and customCompsPlacement. If not, return the default layout and default compsPlacement
 */
const validateAndReturnPageLayoutAndCompsPlacement = (
    entityClass: EntityClass | undefined,
    customLayout: EntityDetailsPageLayout | undefined,
    customCompsPlacement: EntityDetailsCompsPlacement | undefined,
    defaultPageLayout: EntityDetailsPageLayout,
    defaultCompsPlacement: EntityDetailsCompsPlacement,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): [EntityDetailsPageLayout, EntityDetailsCompsPlacement] => {
    if (customLayout && customCompsPlacement) {
        // If both customLayout and custom componentsPlacement are provided, need to make sure the custom components placement uses only the
        // area defined in the customLayout. If not then we will not use the provided customLayout or custom components placement
        if (isValidComponentsPlacement(customLayout, customCompsPlacement, errorLogger, [...(currentConfigPath ?? [])])) {
            return [customLayout, customCompsPlacement];
        }
    }
    if (customCompsPlacement) {
        // If there is no customLayout we will use default layout. But we will also need to validate the custom componentsPlacement to make
        // sure it uses the area defined in the default layout.
        if (isValidComponentsPlacement(
            defaultPageLayout, customCompsPlacement, errorLogger, [...(currentConfigPath ?? [])],
        )) {
            return [defaultPageLayout, customCompsPlacement];
        }
    } else if (customLayout) {
        errorLogger?.logError({
            message: 'If "pageLayout" is customized, "componentsPlacement" is required',
            value  : customCompsPlacement,
            path   : [...(currentConfigPath ?? [])],
        });
    }

    // If neither customLayout or componentsPlacement are provided then we will return the default layout and default components placement.
    // Also, if only customLayout is provided then we also return default layout and components placement and ignore the customLayout. The reason
    // is because componentsPlacement needs to be based on the layout's defined areas. So if there is a customLayout but no customPlacement, we
    // can't use default componentsPlacement because the default componentsPlacement most likely won't work with the specified customLayout.
    return [defaultPageLayout, defaultCompsPlacement];
};



/**
 * Storage structure for a View. Each view will have a layout and componentsPlacement. We will also keep a reference of the 'viewSettings'
 * we get from the entityClass's uiSettings so that we can use it it in some places.
 */
export interface EntityViewConfig {
    readonly layout: EntityDetailsPageLayout;
    readonly componentsPlacement: EntityDetailsCompsPlacement;
    readonly viewSettings: EntityClassViewSettings;
}


/**
 * Check an entityClass' uiSettings and return an Array of EntityViewConfig. This hook will validate the uiSettings and make sure all
 * the settings are valid. If all the settings are valid, it will create and return an array of EntityViewConfig. Each EntityViewConfig
 * contains the layout and componentsPlacement for each view and this is the final config. We can use this config without having to
 * keep checking and validating the uiSettings from the entityClass.
 */
export const useGetEntityClassViewsConfig = (projectId: number, entityClass: EntityClass | undefined): readonly EntityViewConfig[] => {

    const { state:dataState } = useContext(ritzDataContext);
    const project = useSelectProject(projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;


    // Get the entityClassUISettings from project UI Settings
    const projectEntityClassUISettings = useMemo(() => {
        return ProjectTypeHandlersManager.getInstance()
            .getProjectCustomUIHandler(project?.supportToolSettings.projectType)
            ?.getCustomUIConfig(loggedInAgent, project)?.entityClassUISettings;
    }, [loggedInAgent, project]);


    /**
     * Get the viewsSettings from the entityClass's uiSettings. If viewsSettings is not provided in the entityClass then we will
     * use the default view settings. Then for each view settings, get the pageLayout and componentsPlacement for that view. Return
     * the config for ALL views.
     */
    const allViewsConfig = useMemo((): readonly EntityViewConfig[] => {

        // If we have not fetched entityClasses yet, don't return default view config. Return empty array and wait for entity class to be loaded.
        if (!entityClass) {
            return [];
        }

        // Get the uiSettings entityClass. If it is not provided, use the project's entityClassUISettings.
        const uiSettings = entityClass?.displaySettings.uiSettings ?? projectEntityClassUISettings;

        // Create a dummy viewSettings config. We just need this as a placeholder for the default case when the entityClass does
        // not have a customized viewsSettings. We don't use this data at all.
        const defaultViewSettings: EntityClassViewSettings = {
            isReportView: true,
        };


        const errorLogger = new SettingsErrorLogger(`entity class "${entityClass.entityClass}"`, uiSettings ?? {});
        if (uiSettings && isEntityClassUISettings(uiSettings, errorLogger, [])) {

            // Get the custom view settings array from uiSettings. If this array is empty or not provided, create an array of
            // EntityClassViewSettings with only 1 item, defaultViewSettings
            const customViewsSettings: readonly EntityClassViewSettings[] = uiSettings.viewsSettings && uiSettings.viewsSettings.length > 0
                ? uiSettings.viewsSettings
                : [defaultViewSettings];

            // For each custom viewSettings, get the pageLayout and componentsPlacement and return a ViewConfig object containing
            // all these settings.
            const viewsConfig = customViewsSettings.map((viewSettings, viewIndex): EntityViewConfig => {
                const layout = viewSettings.layoutSettings?.pageLayout;
                if (layout) {
                    // The 'layout' specified in the entityClass uiSettings can be a string or an actual custom EntityDetailsPageLayout object.
                    // If it is a string, then it must be the name of one of the predefined layouts in Ritz. We will try to look up the predefined
                    // layout by this name. If we can find it then we will use it. If not then we will use the default layout. Since the name is
                    // set by the developer/agent manually, it is possible the name is wrong so we have to have a fallback.
                    const layoutObject = typeof layout === 'string'
                        ? ALL_PREDEFINED_ENTITY_DETAILS_LAYOUTS[layout]
                        : layout;   // 'layout' is a custom EntityDetailsPageLayout object

                    const [resultLayout, compsPlacement] = validateAndReturnPageLayoutAndCompsPlacement(
                        entityClass,
                        layoutObject,
                        viewSettings.layoutSettings?.componentsPlacement,
                        DEFAULT_ENTITY_DETAILS_LAYOUT, DEFAULT_ENTITY_DETAILS_COMPONENT_PLACEMENT,
                        errorLogger,
                        ['viewSettings', `[${viewIndex}]`, 'layoutSettings', 'componentsPlacement'],
                    );

                    // Apply pageLayoutCustomization to the resultLayout before returning the final layout object
                    return {
                        viewSettings,
                        layout             : applyLayoutCustomization(resultLayout, viewSettings.layoutSettings?.pageLayoutCustomization),
                        componentsPlacement: compsPlacement,
                    };
                }

                // If layout is not specified in the uiSettings then see if it has componentsPlacement settings. If it has componentsPlacement
                // specified and if the componentsPlacement settings is valid then use it.
                const [resultLayout, compsPlacement] = validateAndReturnPageLayoutAndCompsPlacement(
                    entityClass,
                    undefined,
                    viewSettings.layoutSettings?.componentsPlacement,
                    DEFAULT_ENTITY_DETAILS_LAYOUT, DEFAULT_ENTITY_DETAILS_COMPONENT_PLACEMENT,
                    errorLogger,
                    ['viewSettings', `[${viewIndex}]`, 'layoutSettings', 'componentsPlacement'],
                );

                // Apply pageLayoutCustomization to the resultLayout before returning the final layout object
                return {
                    layout             : applyLayoutCustomization(resultLayout, viewSettings.layoutSettings?.pageLayoutCustomization),
                    componentsPlacement: compsPlacement,
                    viewSettings,
                };
            });

            return viewsConfig;
        }

        // If uiSettings is not provided then return the default view config which uses the default layout and default componentsPlacement
        return [{
            layout             : DEFAULT_ENTITY_DETAILS_LAYOUT,
            componentsPlacement: DEFAULT_ENTITY_DETAILS_COMPONENT_PLACEMENT,
            viewSettings       : defaultViewSettings,
        }];
    }, [entityClass, projectEntityClassUISettings]);

    return allViewsConfig;
};
